import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Avatar,
  Input,
  Button,
  Typography,
  Form,
  message,
  Progress,
} from "antd";
import { EditOutlined, UserOutlined } from "@ant-design/icons";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import {
  fetchAccountDisplayData,
  updateAccountProfile,
  updateAccountPassword,
  removeLogo,
} from "src/services/MyAccountAPIs";
import { useUser } from "src/contexts/UserContext";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledPasswordInput from "src/components/reusable/StyledPasswordInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/useLanguage";
import { industryOptions } from "src/utils/industryOptions";

interface User {
  user_id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string; 
    role_name?: string;
  profile_image_url?: string;
}
const { Link } = Typography;

const AccountSettings: React.FC = () => {
  const { t } = useTranslation();
  useLanguage();
  const [profileImage, setProfileImage] = useState<string | undefined>(
    undefined
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { user, setUser } = useUser();
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [industry, setIndustry] = useState("");
const roleId = localStorage.getItem("role_id");
const roleIdNum = roleId ? parseInt(roleId, 10) : 0;
const isAdmin = roleIdNum === 1; 
const isViewer = roleIdNum === 2; 
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [editingIndustry, setEditingIndustry] = useState(false);
  const [company, setCompany] = useState<string>("");
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const hasFetched = useRef(false);
  const [contactNumber, setContactNumber] = useState<string | undefined>(
    undefined
  );
  const [photoRemoved, setPhotoRemoved] = useState(false);
  const [, setIsContactNumberDisabled] = useState<boolean>(false);
  const [firstNameError, setFirstNameError] = useState<string>("");
  const [lastNameError, setLastNameError] = useState<string>("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState<boolean>(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [editingFirst, setEditingFirst] = useState(false);
  const [editingLast, setEditingLast] = useState(false);

  const getPasswordStrength = () => {
    if (
      newPassword.length > 8 &&
      /\d/.test(newPassword) &&
      /[A-Z]/.test(newPassword)
    ) {
      return {
        percent: 100,
        status: "success",
        text: t("accountSettings.passwordStrength.strong"),
      };
    } else if (newPassword.length > 5) {
      return {
        percent: 60,
        status: "active",
        text: t("accountSettings.passwordStrength.medium"),
      };
    } else if (newPassword.length > 0) {
      return {
        percent: 30,
        status: "exception",
        text: t("accountSettings.passwordStrength.weak"),
      };
    }
    return { percent: 0, status: "normal", text: "" };
  };

  const passwordStrength = getPasswordStrength();

  const validateNames = () => {
    let isValid = true;

    if (!firstName || firstName.length < 2) {
      setFirstNameError(t("accountSettings.errors.firstNameMin"));
      isValid = false;
    } else {
      setFirstNameError("");
    }

    if (!lastName || lastName.length < 2) {
      setLastNameError(t("accountSettings.errors.lastNameMin"));
      isValid = false;
    } else {
      setLastNameError("");
    }

    return isValid;
  };

  const validatePassword = () => {
    const passwordRegex = /^(?=.*[!@#$%^&*])(?=.*[0-9])(?=.*[A-Z]).{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      setPasswordError(
        t("accountSettings.errors.passwordRequirements")
      );
      return false;
    }
    setPasswordError("");
    return true;
  };

  const resizeImageToSquare = (file: File, size = 200): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        reject("Canvas not supported");
        return;
      }

      ctx.fillStyle = "#ffffff"; 
      ctx.fillRect(0, 0, size, size);

      const scale = Math.min(size / img.width, size / img.height);
      const newWidth = img.width * scale;
      const newHeight = img.height * scale;

      const dx = (size - newWidth) / 2;
      const dy = (size - newHeight) / 2;

      ctx.drawImage(img, dx, dy, newWidth, newHeight);

      canvas.toBlob(blob => {
        if (blob) {
          const resizedFile = new File([blob], file.name, { type: file.type });
          resolve(resizedFile);
        } else {
          reject("Failed to convert canvas to Blob");
        }
      }, file.type);
    };

    img.onerror = () => reject("Failed to load image");
  });
};

  const fetchData = async () => {
    try {
       const data = await fetchAccountDisplayData();
    setEmail(data.email || "");
    setCompany(data.company_name || "");
    setContactNumber(data.phone_number || "");
    setFirstName(data.first_name || "");
    setLastName(data.last_name || "");
    setWebsiteUrl(data.website_url || "");
    setIndustry(data.industry || "");
    setIsContactNumberDisabled(false);
    if (!photoRemoved) {
      setProfileImage(data.profile_image_url || undefined);
      setUser(prev => ({
        ...prev,
        ...data,
        profile_image_url: data.profile_image_url || undefined,
      }));
      if (data.profile_image_url) {
        localStorage.setItem("profile_image_url", data.profile_image_url);
      } else {
        localStorage.removeItem("profile_image_url");
      }
    }
    } catch (error) {
      console.error("Display data fetch error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : t("accountSettings.errors.fetchDisplayData");
      message.error(errorMessage);
    }
  };

  useEffect(() => {
    if (hasFetched.current) {return;}
    hasFetched.current = true;
    fetchData();
  }, []);

  const handleProfileUpdate = async () => {
    if (!validateNames()) {return;}
    setIsUpdatingProfile(true);
    try {
      await updateAccountProfile(
        firstName,
        lastName,
        contactNumber || "",
        selectedFile || undefined,
        isAdmin ? websiteUrl : undefined,
        isAdmin ? industry : undefined
      );
      message.success(t("accountSettings.messages.profileUpdated"));
      setEditingFirst(false);
      setEditingLast(false);
      setEditingWebsite(false);
      setEditingIndustry(false);
      await fetchData();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("accountSettings.errors.updateProfile");
      message.error(errorMessage);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
  try {
    const resizedFile = await resizeImageToSquare(file, 200);

    setProfileImage(URL.createObjectURL(resizedFile));
    setSelectedFile(resizedFile);

    await updateAccountProfile(
      firstName,
      lastName,
      contactNumber || "",
      resizedFile,
      isAdmin ? websiteUrl : undefined,
      isAdmin ? industry : undefined
    );

    message.success(t("accountSettings.messages.photoUploaded"));
    fetchData();
  } catch (error) {
    console.error(error);
    message.error(t("accountSettings.errors.resizeImage"));
  }
};

const handleRemovePhoto = async () => {
  try {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    const userId = user?.user_id || userData?.user_id || localStorage.getItem("user_id");
    if (!userId) {
      message.error(t("accountSettings.errors.userIdNotFound"));
      return;
    }
    const res = await removeLogo({ user_id: userId });
    if (res.status) {
      message.success(
        res.message || t("accountSettings.messages.logoRemoved")
      );
      setProfileImage(undefined);
      localStorage.removeItem("profile_image_url");
      setIsRemoveModalOpen(false);
      fetchData(); 
    } else {
      message.error(res.message || t("accountSettings.errors.removeLogo"));
    }
  } catch (err) {
    message.error(t("accountSettings.errors.removeLogoGeneric"));
  }
};
  const handlePasswordChange = async () => {
    if (!validatePassword()) {return;}
    if (newPassword !== confirmPassword) {
      message.error(t("accountSettings.errors.passwordsNoMatch"));
      return;
    }
    setIsUpdatingPassword(true);
    try {
      await updateAccountPassword(currentPassword, newPassword);
      message.success(t("accountSettings.messages.passwordUpdated"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : t("accountSettings.errors.updatePassword");
      message.error(errorMessage);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  return (
    <div className="account-settings-container">

      {/* Left Card */}
      <Card title={t("accountSettings.accountInfoTitle")} className="account-card">
        <div className="account-avatar-section">
         <Avatar
  size={120}
  className={`account-avatar ${!profileImage ? "no-photo" : "has-photo"}`}
  src={profileImage}
  icon={!profileImage && <UserOutlined />}
/>
          <div className="account-photo-actions">
  <input
  type="file"
  accept="image/*"
  id="profile-upload"
  className="profile-upload-input"
  onClick={e => {
    if (!isAdmin) {
      e.preventDefault(); 
      message.warning(t("accountSettings.warnings.adminOnlyPhoto"));
    }
  }}
  onChange={e => {
    if (!isAdmin) {return;}
    const file = e.target.files?.[0];
    if (file) {
      handlePhotoUpload(file);
    }
  }}
/>

<label htmlFor="profile-upload" className="photo-label">
  <Link className="photo-btns">{t("accountSettings.uploadPhoto")}</Link>
</label>
{profileImage && (
  <>
    <span className="divider">|</span>
    <label className="photo-label">
      <Link
  onClick={() => {
    if (!isAdmin) {
      message.warning(t("accountSettings.warnings.adminOnlyPhoto"));
      return;
    }
    handleRemovePhoto();
  }}
  className="photo-btns"
>
  {t("accountSettings.removePhoto")}
</Link>

    </label>
  </>
)}
          </div>
        </div>

        <Form layout="vertical" className="account-form">
          <Form.Item label={t("accountSettings.labels.firstName")}>
            <StyledTextInput
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              disabled={!editingFirst}
              suffix={
                <EditOutlined
                  onClick={() => setEditingFirst(!editingFirst)}
                  className="edit-icon"
                />
              }
            />
            {firstNameError && (
              <div className="error-message">{firstNameError}</div>
            )}
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.lastName")}>
            <StyledTextInput
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              disabled={!editingLast}
              suffix={
                <EditOutlined
                  onClick={() => setEditingLast(!editingLast)}
                  className="edit-icon"
                />
              }
            />
            {lastNameError && (
              <div className="error-message">{lastNameError}</div>
            )}
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.email")}>
            <StyledTextInput value={email} disabled />
          </Form.Item>

<Form.Item label={t("accountSettings.labels.websiteUrl")}>
  <StyledTextInput
    value={websiteUrl}
    onChange={e => setWebsiteUrl(e.target.value)}
    disabled={isViewer || !editingWebsite} 
    onClick={() => {
      if (isViewer) {
        message.warning(t("accountSettings.warnings.adminOnlyWebsite"));
      }
    }}
    suffix={
      isAdmin && (
        <EditOutlined
          onClick={() => setEditingWebsite(prev => !prev)}
          className="edit-icon"
        />
      )
    }
  />
</Form.Item>

          <Form.Item label={t("accountSettings.labels.company")}>
            <StyledTextInput value={company} disabled />
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.industry")}>
            <StyledDropdown
              value={industry || undefined}
              className="account-industry-dropdown"
              placeholder={t("userIntro.selectIndustry")}
              onChange={value => setIndustry(value as string)}
              disabled={isViewer || !editingIndustry}
              onClick={() => {
                if (isViewer) {
                  message.warning(t("accountSettings.warnings.adminOnlyIndustry"));
                }
              }}
              options={industryOptions.map(option => ({
                label: option,
                value: option,
              }))}
              suffixIcon={
                isAdmin ? (
                  <span className="industry-suffix-icons">
                    <span className="styled-dropdown-caret" aria-hidden="true" />
                    <EditOutlined
                      className="industry-edit-icon"
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        setEditingIndustry(prev => !prev);
                      }}
                    />
                  </span>
                ) : (
                  <span className="styled-dropdown-caret" aria-hidden="true" />
                )
              }
            />
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.contactNumber")}>
            <StyledTextInput value={contactNumber} disabled />
          </Form.Item>

          <Button
            type="primary"
            className="button button--primary setting-btn"
            onClick={handleProfileUpdate}
            loading={isUpdatingProfile}
          >
            {isUpdatingProfile
              ? t("accountSettings.updating")
              : t("accountSettings.update")}
          </Button>
        </Form>
      </Card>

      {/* Right Card */}
      <Card title={t("accountSettings.changePasswordTitle")} className="password-card">
        <Form layout="vertical" className="password-form">
          <Form.Item label={t("accountSettings.labels.currentPassword")}>
            <StyledPasswordInput
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.newPassword")}>
            <StyledPasswordInput
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            {passwordStrength.text && (
              <div className="password-strength">
                <Progress
                  percent={passwordStrength.percent}
                  status={passwordStrength.status as any}
                  showInfo={false}
                />
                <span className={`strength-text ${passwordStrength.status}`}>
                  {passwordStrength.text}
                </span>
              </div>
            )}
            {passwordError && (
              <div className="error-message">{passwordError}</div>
            )}
          </Form.Item>

          <Form.Item label={t("accountSettings.labels.confirmPassword")}>
            <StyledPasswordInput
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </Form.Item>

          <Button
            type="primary"
            className="button button--primary setting-btn"
            onClick={handlePasswordChange}
            loading={isUpdatingPassword}
          >
            {isUpdatingPassword
              ? t("accountSettings.updating")
              : t("accountSettings.updatePassword")}
          </Button>
        </Form>
      </Card>

      <ReusableModal
        modalOpen={isRemoveModalOpen}
        onCancel={() => setIsRemoveModalOpen(false)}
        onConfirm={handleRemovePhoto}
        title={t("accountSettings.removePhotoTitle")}
        description={t("accountSettings.removePhotoDescription")}
        icon="/Icons/remove-photo.svg"
        confirmText={t("accountSettings.confirmYes")}
        cancelText={t("accountSettings.cancel")}
      />
    </div>
  );
};

export default AccountSettings;
